import { UseQueryResult } from '@tanstack/react-query';
import { tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';
import { useQuery } from '@/hooks/useQuery';

/**
 * Dashboard statistics data interface
 */
export interface DashboardStats {
  students: number;
  classes: number;
  families: number;
  activeStudents?: number;
  freeTrialStudents?: number;
  staffMembers?: number;
}

/**
 * Hook to fetch dashboard statistics with performance optimizations
 * and improved error handling
 */
export const useDashboardStats = (): UseQueryResult<DashboardStats, Error> => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useDashboardStats');
  
  // Memoize the organizationId to prevent unnecessary query invalidations
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['dashboardStats', organizationId] as const,
    queryFn: async (): Promise<DashboardStats> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchDashboardStats',
          { userId: user?.id },
          false // Don't show toast here, let React Query handle it
        );
      }

      try {
        // Use Promise.all for parallel queries with proper typing
        const [classesResult, studentsResult, familiesResult, staffResult] = await Promise.all([
          tables.classes().select('id, status, is_active').eq('organization_id', organizationId).eq('status', 'active').eq('is_active', true),
          tables.students().select('id').eq('organization_id', organizationId),
          tables.families().select('id').eq('organization_id', organizationId),
          tables.staff_members().select('id').eq('organization_id', organizationId)
        ]);

        // Check for errors in any of the queries with proper type handling
        if (classesResult.error) throw classesResult.error;
        if (studentsResult.error) throw studentsResult.error;
        if (familiesResult.error) throw familiesResult.error;
        if (staffResult.error) throw staffResult.error;

        // Debug logging to track data
        console.log('useDashboardStats - Raw data:', {
          classes: classesResult.data,
          classesCount: classesResult.data?.length || 0,
          students: studentsResult.data?.length || 0,
          families: familiesResult.data?.length || 0,
          staffMembers: staffResult.data,
          staffMembersCount: staffResult.data?.length || 0,
          organizationId
        });

        // Create the stats object with safe access to data arrays
        const stats: DashboardStats = {
          students: studentsResult.data?.length || 0,
          classes: classesResult.data?.length || 0,
          families: familiesResult.data?.length || 0,
          staffMembers: staffResult.data?.length || 0
        };

        // Fetch active students (students with at least one active enrollment)
        const { data: activeEnrollments } = await tables.enrollments()
          .select('student_id')
          .eq('organization_id', organizationId)
          .eq('status', 'active');
        const activeStudentIds = Array.from(new Set((activeEnrollments || []).map(e => e.student_id)));

        const finalStats = {
          students: stats.students,
          classes: stats.classes,
          families: stats.families,
          activeStudents: activeStudentIds.length,
          staffMembers: stats.staffMembers,
        };
        
        // Debug logging for dashboard stats
        console.log("useDashboardStats RESULT:", {
          finalStats,
          rawCounts: {
            classesCount: classesResult.data?.length || 0,
            studentsCount: studentsResult.data?.length || 0,
            familiesCount: familiesResult.data?.length || 0,
            staffCount: staffResult.data?.length || 0,
            activeEnrollmentsCount: activeEnrollments?.length || 0,
            activeStudentIdsCount: activeStudentIds.length
          },
          organizationId,
          timestamp: new Date().toISOString()
        });
        
        return finalStats;
      } catch (error) {
        // Categorize database errors correctly
        throw errorHandler.handleError(
          error,
          'fetchDashboardStats',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false // Don't show toast here, let React Query handle it
        );
      }
    },
    options: {
      // Original cache settings (for slower updates):
      // staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      // gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
      
      // Updated cache settings (for faster updates):
      staleTime: 30 * 1000, // Consider data fresh for 30 seconds
      gcTime: 5 * 60 * 1000, // Keep unused data in cache for 5 minutes
      retry: 2,
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Always refetch on mount
      meta: {
        onError: (error: Error) => {
          // Let React Query handle the error toast
          errorHandler.handleError(
            error,
            'Failed to load dashboard statistics',
            { userId: user?.id, organizationId },
            true
          );
        }
      },
      // Only run the query when we have an organizationId
      enabled: !!organizationId
    },
    errorHandlingOptions: {
      componentName: 'useDashboardStats',
      operationName: 'fetchDashboardStats',
      category: ErrorCategory.DATABASE,
      showToast: true,
      additionalData: { organizationId }
    }
  });
};
